import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import DataLoader from "../../../../Utils/DataLoader";
import { course_all } from "../../../api/Course/Course";
import { get_classes_by_course } from "../../../api/Class";
import { courseAllowed, getBranchLevel } from "../../../utils/branchLevel";

const AllCourses = () => {
  const [courses, setCourses] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [classesMap, setClassesMap] = useState({});
  const [loadingClasses, setLoadingClasses] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const branchid = localStorage.getItem("b_id");
  const level = getBranchLevel();
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    async function loadCourses() {
      const result = await course_all();
      setCourses(result?.data || []);
      setShowResults(true);
    }
    loadCourses();
  }, []);

  const visibleCourses = useMemo(() => {
    const filtered = (courses || []).filter((c) => {
      if (!courseAllowed(c.grade, c.high_school)) return false;
      if (!showArchived) {
        // Hide past years and inactive by default.
        if (c.year && Number(c.year) < currentYear) return false;
        if (String(c.status) !== "1") return false;
      }
      return true;
    });
    // Sort by grade asc (treating "Kindy" / 0 as the lowest).
    return filtered.sort((a, b) => (Number(a.grade) || 0) - (Number(b.grade) || 0));
  }, [courses, showArchived, currentYear]);

  const toggleCourse = async (courseId) => {
    if (expandedCourseId === courseId) {
      setExpandedCourseId(null);
      return;
    }
    setExpandedCourseId(courseId);
    if (!classesMap[courseId]) {
      setLoadingClasses(courseId);
      const res = await get_classes_by_course(branchid, courseId);
      setClassesMap((prev) => ({ ...prev, [courseId]: res?.data || [] }));
      setLoadingClasses(null);
    }
  };

  const s = {
    page: { padding: "20px 24px" },
    header: {
      display: "flex", justifyContent: "space-between", alignItems: "center",
      marginBottom: 20,
    },
    title: { fontSize: 18, fontWeight: 600, margin: 0, color: "#1a1a2e" },
    // course row
    courseRow: (isExpanded) => ({
      display: "grid",
      gridTemplateColumns: "100px 1fr 80px 60px 40px",
      gap: 8, alignItems: "center",
      padding: "10px 14px", cursor: "pointer", userSelect: "none",
      background: isExpanded ? "#f8fafc" : "#fff",
      borderBottom: isExpanded ? "none" : "1px solid #f0f0f0",
      transition: "background 0.15s",
    }),
    courseName: { flex: 1, fontSize: 13, fontWeight: 500, color: "#1e293b" },
    courseGrade: {
      fontSize: 11, fontWeight: 500, color: "#64748b",
      padding: "2px 10px", borderRadius: 12, background: "#f1f5f9",
    },
    courseBadge: (active) => ({
      display: "inline-block", padding: "2px 10px", borderRadius: 12,
      fontSize: 11, fontWeight: 500,
      background: active ? "#dcfce7" : "#fee2e2",
      color: active ? "#166534" : "#991b1b",
    }),
    courseAction: {
      width: 28, height: 28, borderRadius: 6, border: "1px solid #e5e7eb",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: "#fff", color: "#666", cursor: "pointer", fontSize: 12,
      padding: 0, textDecoration: "none",
    },
    chevron: { fontSize: 10, color: "#999", width: 20, textAlign: "center", flexShrink: 0 },
    // child panel
    childPanel: {
      margin: "0 0 0 32px", padding: "10px 16px",
      borderLeft: "3px solid #8b5cf6", background: "#fafbfc",
      borderBottom: "1px solid #f0f0f0",
    },
    childLabel: {
      fontSize: 11, fontWeight: 500, color: "#999",
      marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    },
    addClassBtn: {
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 500,
      background: "#8b5cf6", color: "#fff", border: "none",
      textDecoration: "none", cursor: "pointer",
    },
    classTable: {
      borderRadius: 6, overflow: "hidden", border: "1px solid #e9ecef",
      background: "#fff",
    },
    classHeader: {
      display: "grid", gridTemplateColumns: "1fr 80px 70px 90px 60px",
      gap: 8, padding: "6px 12px", background: "#f8f9fa",
      borderBottom: "1px solid #e9ecef",
      fontSize: 11, fontWeight: 600, color: "#888",
      textTransform: "uppercase", letterSpacing: "0.3px",
    },
    classRow: {
      display: "grid", gridTemplateColumns: "1fr 80px 70px 90px 60px",
      gap: 8, padding: "8px 12px", borderBottom: "1px solid #f5f5f5",
      fontSize: 12, alignItems: "center",
    },
    className: {
      color: "#3b82f6", textDecoration: "none", fontWeight: 500, fontSize: 12,
    },
    studentCount: {
      fontSize: 11, color: "#64748b",
    },
    emptyMsg: {
      padding: "16px 12px", fontSize: 12, color: "#999", textAlign: "center",
    },
    loadingMsg: {
      padding: "12px", fontSize: 12, color: "#999", textAlign: "center",
    },
    tableWrap: {
      borderRadius: 8, border: "1px solid #e9ecef", overflow: "hidden",
      background: "#fff",
    },
    tableHeader: {
      display: "grid", gridTemplateColumns: "100px 1fr 80px 60px 40px",
      gap: 8, padding: "8px 14px", background: "#f1f1f1",
      fontSize: 13, fontWeight: 600, color: "#333",
    },
  };

  return (
    <div className="right-nav" id="dash-event" style={s.page}>
      {showResults ? (
        <>
          <div style={s.header}>
            <h4 style={s.title}>
              Courses
              <span style={{ fontSize: 11, fontWeight: 500, color: "#64748b", marginLeft: 8 }}>
                ({level === "high" ? "High school" : "Primary"})
              </span>
            </h4>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived (past years &amp; inactive)
            </label>
          </div>

          <div style={s.tableWrap}>
            {/* Table Header */}
            <div style={s.tableHeader}>
              <span>Grade</span>
              <span>Course</span>
              <span>Status</span>
              <span></span>
              <span></span>
            </div>

            {visibleCourses.length === 0 && (
              <div style={{ padding: "20px 14px", color: "#94a3b8", fontSize: 13 }}>
                No courses to show. {!showArchived && courses.length > 0 ? "Try enabling \"Show archived\"." : ""}
              </div>
            )}
            {visibleCourses.map((course) => {
              const isExpanded = expandedCourseId === course.id;
              const classes = classesMap[course.id] || [];
              const isLoading = loadingClasses === course.id;
              return (
                <div key={course.id}>
                  {/* Course Row */}
                  <div
                    style={s.courseRow(isExpanded)}
                    onClick={() => toggleCourse(course.id)}
                  >
                    <span style={s.courseGrade}>
                      {course.grade === 0 ? "Kindy" : `Year ${course.grade}`}
                    </span>
                    <div style={s.courseName}>{course.name}</div>
                    <span style={s.courseBadge(course.status == 1)}>
                      {course.status == 1 ? "Active" : "Inactive"}
                    </span>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <Link
                        to={`/branch/course/Details/${course.id}`}
                        style={s.courseAction}
                        title="View Details"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <i className="fa fa-eye"></i>
                      </Link>
                    </div>
                    <div style={s.chevron}>
                      <i className={`fa fa-chevron-${isExpanded ? "up" : "down"}`}></i>
                    </div>
                  </div>

                  {/* Expanded: Classes List */}
                  {isExpanded && (
                    <div style={s.childPanel}>
                      <div style={s.childLabel}>
                        <span>Classes ({isLoading ? "..." : classes.length})</span>
                        <Link
                          to="/branch/class-management/add"
                          state={{ prefillCourseId: course.id }}
                          style={s.addClassBtn}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="fa fa-plus" style={{ fontSize: 9 }}></i> Add Class
                        </Link>
                      </div>
                      {isLoading ? (
                        <div style={s.loadingMsg}>Loading...</div>
                      ) : classes.length === 0 ? (
                        <div style={s.emptyMsg}>No classes found for this course</div>
                      ) : (
                        <div style={s.classTable}>
                          <div style={s.classHeader}>
                            <span>Class Name</span>
                            <span>Year</span>
                            <span>Term</span>
                            <span>Students</span>
                            <span>Status</span>
                          </div>
                          {classes.map((cls) => (
                            <div key={cls.id} style={s.classRow}>
                              <Link
                                to={`/branch/class-management/edit/${cls.id}`}
                                style={s.className}
                              >
                                {cls.class_name}
                              </Link>
                              <span style={{ fontSize: 12, color: "#555" }}>{cls.year}</span>
                              <span style={{ fontSize: 12, color: "#555" }}>{cls.term_name || "-"}</span>
                              <span style={s.studentCount}>
                                {cls.student_count} student{cls.student_count !== 1 ? "s" : ""}
                              </span>
                              <span style={{
                                width: 8, height: 8, borderRadius: "50%",
                                background: cls.delete_status === 1 ? "#22c55e" : "#ef4444",
                                display: "inline-block",
                              }}></span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <DataLoader />
      )}
    </div>
  );
};
export default AllCourses;
